import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MouseIcon from '@mui/icons-material/Mouse';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BoltIcon from '@mui/icons-material/Bolt';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  SearchConsoleSummary,
  SeoOpportunities,
  SeoOpportunity,
} from '@/services/searchConsole';
import SpeedIcon from '@mui/icons-material/Speed';
import PeopleIcon from '@mui/icons-material/PeopleOutline';
import AiTitleMetaRewriter from '@/components/AiTitleMetaRewriter';
import WhatWorkedInsights from '@/components/WhatWorkedInsights';
import { GA4DashboardSummary } from '@/services/googleAnalytics';
import { PageSpeedResult } from '@/services/pageSpeed';

type Props = {
  siteUrl: string | null;
  searchConsoleSummary: SearchConsoleSummary | null;
  opportunities: SeoOpportunities | null;
  ga4Summary: GA4DashboardSummary | null;
  pageSpeed: PageSpeedResult | null;
  isLoadingConnection: boolean;
  isLoadingSummary: boolean;
  isLoadingOpportunities: boolean;
  isLoadingGa4: boolean;
  isLoadingPageSpeed: boolean;
};

const fmtNum = (n: number): string => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const pctChange = (cur: number, prev: number): number => {
  if (prev === 0) return cur > 0 ? 100 : 0;
  return ((cur - prev) / prev) * 100;
};

const ChangeIndicator = ({ cur, prev, invert }: { cur: number; prev: number; invert?: boolean }) => {
  const change = pctChange(cur, prev);
  if (Math.abs(change) < 0.5) return null;
  const isPositive = invert ? change < 0 : change > 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: isPositive ? 'success.main' : 'error.main' }}>
      {isPositive ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
      <Typography variant="caption" fontWeight={600}>{Math.abs(change).toFixed(0)}%</Typography>
    </Box>
  );
};

const priorityColor: Record<string, 'error' | 'warning' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

const showAiRewriter = (type: string) => type === 'low_ctr' || type === 'quick_win';

const OpportunityCard = ({ opp }: { opp: SeoOpportunity }) => (
  <Box
    sx={{
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      '&:hover': { borderColor: '#054AD920', backgroundColor: '#fafafa' },
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
        {opp.title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Chip label={opp.priority} color={priorityColor[opp.priority]} size="small" sx={{ fontSize: 11, height: 20 }} />
        <Chip label={opp.metric} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
      </Box>
    </Box>
    <Typography variant="caption" color="text.secondary">{opp.description}</Typography>
    {showAiRewriter(opp.type) && opp.keyword && (
      <AiTitleMetaRewriter
        keyword={opp.keyword}
        pageUrl={opp.page}
        position={opp.position}
        ctr={opp.ctr}
        impressions={opp.impressions}
      />
    )}
  </Box>
);

type OpportunitySectionProps = {
  icon: typeof GpsFixedIcon;
  title: string;
  subtitle: string;
  color: string;
  items: SeoOpportunity[];
};

const OpportunitySection = ({ icon: Icon, title, subtitle, color, items }: OpportunitySectionProps) => {
  const [showAll, setShowAll] = useState(false);
  if (items.length === 0) return null;
  const visible = showAll ? items : items.slice(0, 3);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Icon sx={{ fontSize: 20, color }} />
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        <Chip label={items.length} size="small" sx={{ fontSize: 11, height: 20, ml: 0.5 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {subtitle}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {visible.map((opp, i) => (
          <OpportunityCard key={i} opp={opp} />
        ))}
      </Box>
      {items.length > 3 && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ mt: 1.5, display: 'block', cursor: 'pointer', fontWeight: 600 }}
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? 'Show less' : `Show ${items.length - 3} more`}
        </Typography>
      )}
    </Box>
  );
};

const formatSiteUrl = (url: string): string =>
  url.replace('sc-domain:', '').replace(/^https?:\/\//, '').replace(/\/$/, '');

const scoreColor = (score: number) =>
  score >= 90 ? '#0D9D58' : score >= 50 ? '#F4B400' : '#EA4335';

const ScoreCircle = ({ score, label }: { score: number; label: string }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{
      width: 56, height: 56, borderRadius: '50%', border: '3px solid',
      borderColor: scoreColor(score), display: 'flex', alignItems: 'center',
      justifyContent: 'center', mx: 'auto', mb: 0.5,
    }}>
      <Typography variant="body2" fontWeight={700} color={scoreColor(score)}>{score}</Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

const GrowthXDashboardComponent = function ({
  siteUrl,
  searchConsoleSummary: summary,
  opportunities,
  ga4Summary,
  pageSpeed,
  isLoadingConnection,
  isLoadingSummary,
  isLoadingOpportunities,
  isLoadingGa4,
  isLoadingPageSpeed,
}: Props): JSX.Element {
  const { projectId = '' } = useParams();
  const base = projectId ? `/seox/projects/${projectId}` : '/seox';
  const settingsPath = projectId ? `${base}/settings` : '/settings/integrations';
  const quickActions = [
    { label: 'Run Site Audit', icon: BugReportOutlinedIcon, path: `${base}/audits` },
    { label: 'Track Keywords', icon: ManageSearchOutlinedIcon, path: `${base}/keywords` },
    { label: 'Optimize Content', icon: ArticleOutlinedIcon, path: `${base}/content` },
  ];
  const cur = summary?.current;
  const prev = summary?.previous;

  return (
    <Box sx={{}}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TrendingUpOutlinedIcon sx={{ fontSize: 26, color: '#054AD9' }} />
          <Typography variant="h5" fontWeight={700}>SEO Performance</Typography>
          {siteUrl && (
            <Chip label={formatSiteUrl(siteUrl)} size="small" variant="outlined" sx={{ fontSize: 12 }} />
          )}
        </Box>
        <Link to={settingsPath} style={{ textDecoration: 'none' }}>
          <SettingsOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', '&:hover': { color: '#054AD9' } }} />
        </Link>
      </Box>

      {!siteUrl && !isLoadingConnection && (
        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 4, textAlign: 'center', backgroundColor: '#fafafa', mt: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>No website configured</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Go to Settings to connect Google Search Console and select a website to track.
          </Typography>
          <Link to={settingsPath} style={{ textDecoration: 'none' }}>
            <Typography variant="body2" fontWeight={600} color="#054AD9">
              Open Settings
            </Typography>
          </Link>
        </Box>
      )}

      {siteUrl && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Overview for the last 28 days
        </Typography>
      )}

      {/* Loading */}
      {isLoadingSummary && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#054AD9' }} />
        </Box>
      )}

      {/* Key Numbers */}
      {cur && !isLoadingSummary && (
        <>
          <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, display: 'block', letterSpacing: 1.5 }}>
            Key Numbers
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
            {[
              { label: 'Google Clicks', value: fmtNum(cur.totalClicks), color: '#4285F4', prev: prev?.totalClicks },
              { label: 'Impressions', value: fmtNum(cur.totalImpressions), color: '#A142F4', prev: prev?.totalImpressions },
              { label: 'Average CTR', value: `${(cur.avgCtr * 100).toFixed(2)}%`, color: '#0D9D58', prev: prev?.avgCtr, isCtr: true },
              { label: 'Average Position', value: cur.avgPosition.toFixed(1), color: '#F4B400', prev: prev?.avgPosition, invert: true },
            ].map((card) => (
              <Box key={card.label} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2.5, backgroundColor: '#fff' }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
                  <Typography variant="h5" fontWeight={700} color={card.color}>{card.value}</Typography>
                  {card.prev != null && (
                    <ChangeIndicator
                      cur={card.isCtr ? cur.avgCtr : card.invert ? cur.avgPosition : card.label === 'Google Clicks' ? cur.totalClicks : cur.totalImpressions}
                      prev={card.prev}
                      invert={card.invert}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Daily Clicks Trend */}
          {summary!.dailyTrend.length > 0 && (
            <>
              <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, display: 'block', letterSpacing: 1.5 }}>
                Daily Organic Clicks
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 120 }}>
                  {(() => {
                    const maxClicks = Math.max(...summary!.dailyTrend.map((d) => d.clicks), 1);
                    return summary!.dailyTrend.map((day, i) => {
                      const h = Math.max(4, (day.clicks / maxClicks) * 110);
                      return (
                        <Box
                          key={i}
                          sx={{
                            flex: 1, height: h, backgroundColor: '#4285F4',
                            borderRadius: '3px 3px 0 0',
                            opacity: 0.65 + (i / summary!.dailyTrend.length) * 0.35,
                            position: 'relative',
                            '&:hover': { opacity: 1, '& .tip': { display: 'block' } },
                          }}
                        >
                          <Box className="tip" sx={{
                            display: 'none', position: 'absolute', bottom: '100%', left: '50%',
                            transform: 'translateX(-50%)', backgroundColor: 'rgba(0,0,0,0.85)',
                            color: '#fff', px: 1, py: 0.5, borderRadius: 1, whiteSpace: 'nowrap',
                            fontSize: 11, mb: 0.5, zIndex: 10,
                          }}>
                            {day.keys?.[0]} — {day.clicks} clicks
                          </Box>
                        </Box>
                      );
                    });
                  })()}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">{summary!.dailyTrend[0]?.keys?.[0] ?? ''}</Typography>
                  <Typography variant="caption" color="text.secondary">{summary!.dailyTrend[summary!.dailyTrend.length - 1]?.keys?.[0] ?? ''}</Typography>
                </Box>
              </Box>
            </>
          )}

          {/* GA4 Traffic + PageSpeed row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 4 }}>
            {/* GA4 Traffic */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PeopleIcon sx={{ fontSize: 20, color: '#4285F4' }} />
                <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 1.5 }}>
                  Traffic & Users
                </Typography>
              </Box>
              {isLoadingGa4 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={20} sx={{ color: '#4285F4' }} />
                </Box>
              ) : !ga4Summary ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No GA4 data. Connect a GA4 property in Settings.
                  </Typography>
                  <Link to={settingsPath} style={{ textDecoration: 'none' }}>
                    <Typography variant="caption" fontWeight={600} color="#054AD9">Open Settings</Typography>
                  </Link>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Active Users</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h6" fontWeight={700}>{fmtNum(ga4Summary.current.activeUsers)}</Typography>
                        <ChangeIndicator cur={ga4Summary.current.activeUsers} prev={ga4Summary.previous.activeUsers} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Sessions</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h6" fontWeight={700}>{fmtNum(ga4Summary.current.sessions)}</Typography>
                        <ChangeIndicator cur={ga4Summary.current.sessions} prev={ga4Summary.previous.sessions} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Bounce Rate</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h6" fontWeight={700}>{(ga4Summary.current.bounceRate * 100).toFixed(1)}%</Typography>
                        <ChangeIndicator cur={ga4Summary.current.bounceRate} prev={ga4Summary.previous.bounceRate} invert />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Conversions</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h6" fontWeight={700}>{ga4Summary.current.conversions}</Typography>
                        <ChangeIndicator cur={ga4Summary.current.conversions} prev={ga4Summary.previous.conversions} />
                      </Box>
                    </Box>
                  </Box>
                  {/* Channel breakdown */}
                  {ga4Summary.channels.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Traffic by Channel
                      </Typography>
                      {ga4Summary.channels.slice(0, 6).map((ch, i) => {
                        const totalSessions = ga4Summary!.channels.reduce((s, c) => s + c.sessions, 0);
                        const pct = totalSessions > 0 ? ((ch.sessions / totalSessions) * 100).toFixed(1) : '0';
                        return (
                          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2">{ch.channel}</Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <Typography variant="caption" fontWeight={600}>{fmtNum(ch.sessions)}</Typography>
                              <Typography variant="caption" color="text.secondary">{pct}%</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* PageSpeed */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SpeedIcon sx={{ fontSize: 20, color: '#F4B400' }} />
                <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 1.5 }}>
                  PageSpeed (Mobile)
                </Typography>
              </Box>
              {isLoadingPageSpeed ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={20} sx={{ color: '#F4B400' }} />
                </Box>
              ) : !pageSpeed || pageSpeed.error ? (
                <Typography variant="body2" color="text.secondary">
                  {pageSpeed?.error ?? 'Unable to analyze page speed.'}
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 3 }}>
                    <ScoreCircle score={pageSpeed.scores.performance} label="Performance" />
                    <ScoreCircle score={pageSpeed.scores.seo} label="SEO" />
                    <ScoreCircle score={pageSpeed.scores.accessibility} label="A11y" />
                  </Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Core Web Vitals
                  </Typography>
                  {[
                    { label: 'Largest Contentful Paint', data: pageSpeed.coreWebVitals.lcp },
                    { label: 'First Contentful Paint', data: pageSpeed.coreWebVitals.fcp },
                    { label: 'Total Blocking Time', data: pageSpeed.coreWebVitals.tbt },
                    { label: 'Cumulative Layout Shift', data: pageSpeed.coreWebVitals.cls },
                    { label: 'Speed Index', data: pageSpeed.coreWebVitals.si },
                  ].map((item) => (
                    <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2">{item.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" fontWeight={600}>
                          {item.data.displayValue ?? '—'}
                        </Typography>
                        {item.data.score != null && (
                          <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            backgroundColor: item.data.score >= 0.9 ? '#0D9D58' : item.data.score >= 0.5 ? '#F4B400' : '#EA4335',
                          }} />
                        )}
                      </Box>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Box>

          {/* Opportunities */}
          {isLoadingOpportunities && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
              <CircularProgress size={16} sx={{ color: '#054AD9' }} />
              <Typography variant="body2" color="text.secondary">Analyzing opportunities...</Typography>
            </Box>
          )}

          {opportunities && !isLoadingOpportunities && opportunities.totalCount > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RocketLaunchIcon sx={{ fontSize: 20, color: '#054AD9' }} />
                <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 1.5 }}>
                  Growth Opportunities
                </Typography>
                <Chip label={`${opportunities.totalCount} found`} size="small" color="secondary" sx={{ fontSize: 11, height: 22 }} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 4 }}>
                <OpportunitySection
                  icon={BoltIcon}
                  title="Quick Wins"
                  subtitle="Keywords ranking 4-8 with high impressions. Small improvements = big traffic gains."
                  color="#F4B400"
                  items={opportunities.quickWins}
                />
                <OpportunitySection
                  icon={GpsFixedIcon}
                  title="Striking Distance"
                  subtitle="Keywords ranking 8-20. A content refresh or backlink could push these to page 1."
                  color="#054AD9"
                  items={opportunities.strikingDistance}
                />
                <OpportunitySection
                  icon={MouseIcon}
                  title="Low CTR"
                  subtitle="Ranking well but not getting clicked. Improve titles and meta descriptions."
                  color="#4285F4"
                  items={opportunities.lowCtr}
                />
                <OpportunitySection
                  icon={TrendingDownIcon}
                  title="Declining Pages"
                  subtitle="Pages losing traffic vs previous period. Investigate and refresh content."
                  color="#EA4335"
                  items={opportunities.declining}
                />
              </Box>
            </>
          )}

          {/* Top Pages + Top Queries */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 4 }}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff' }}>
              <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ mb: 2, display: 'block', letterSpacing: 1.5 }}>
                Top Pages by Clicks
              </Typography>
              {summary!.topPages.slice(0, 10).map((row, i) => {
                const path = row.keys[0].replace(/^https?:\/\/[^/]+/, '') || '/';
                return (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: i < Math.min(summary!.topPages.length, 10) - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ maxWidth: '50%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.keys[0]}>{path}</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="caption" fontWeight={600}>{row.clicks} clicks</Typography>
                      <Typography variant="caption" color="text.secondary">{fmtNum(row.impressions)} impr</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff' }}>
              <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ mb: 2, display: 'block', letterSpacing: 1.5 }}>
                Top Queries
              </Typography>
              {summary!.topQueries.slice(0, 10).map((row, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: i < Math.min(summary!.topQueries.length, 10) - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ maxWidth: '50%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.keys[0]}</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="caption" fontWeight={600}>{row.clicks} clicks</Typography>
                    <Typography variant="caption" color="text.secondary">#{row.position.toFixed(1)}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* What Worked Insights */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 3, backgroundColor: '#fff', mb: 4 }}>
        <WhatWorkedInsights projectId={projectId} />
      </Box>

      {/* Quick Actions */}
      <Typography variant="overline" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, display: 'block', letterSpacing: 1.5 }}>
        Quick Actions
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <Link key={action.path} to={action.path} style={{ textDecoration: 'none' }}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2, backgroundColor: '#fff', display: 'flex', alignItems: 'center', gap: 1.5, transition: 'all 0.2s', '&:hover': { borderColor: '#054AD9', boxShadow: '0 4px 12px rgba(5,74,217,0.12)' } }}>
                <Icon sx={{ fontSize: 22, color: '#054AD9' }} />
                <Typography variant="body2" fontWeight={600} color="text.primary">{action.label}</Typography>
              </Box>
            </Link>
          );
        })}
      </Box>
    </Box>
  );
};

export default GrowthXDashboardComponent;
